import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart3, Users, Crown, Target, Trophy, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Mentor read-only view of every team's progress.
 * - Shows team members & C-suite roles
 * - Founding business + finalised target (and rationale)
 * - Latest CEO report
 * - Submission count + winning submissions + avg AI score
 * Mentors cannot edit anything from here — they use this to anchor 1:1 advice
 * to where each team currently is in the week.
 */

export default function MentorTeamsProgress() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mentor/teams-progress");
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load teams progress");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-8" data-testid="mentor-teams-progress-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><BarChart3 size={12} /> Mentor read-only view</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Teams&apos; progress</h1>
          <p className="text-[#525252] mt-2 max-w-2xl text-sm leading-relaxed">
            Use this snapshot to see where each team is in the week. You can&apos;t edit anything from here — it&apos;s a
            view-only window so you can tailor your 1:1 advice when teams come to a mentor slot.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 border border-[#0A0A0A] px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#0A0A0A] hover:text-white"
          data-testid="mentor-teams-progress-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[#525252]">Loading teams…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-[#E5E7EB] p-8 text-center text-sm text-[#525252]">
          No teams yet.
        </div>
      ) : (
        <div className="space-y-5">
          {rows.map((row) => {
            const t = row.team;
            return (
              <article key={t.id} className="border border-[#E5E7EB] bg-white p-6" data-testid={`team-progress-${t.id}`}>
                <header className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="label-eyebrow inline-flex items-center gap-2">
                      <Users size={12} /> {t.name}
                      <span className="font-mono bg-[#0A0A0A] text-white px-2 py-0.5 tracking-widest ml-2">{t.join_code}</span>
                    </div>
                    {t.founding_business_name && (
                      <div className="font-display text-2xl font-black tracking-tighter mt-2">{t.founding_business_name}</div>
                    )}
                    {t.sector && <div className="label-eyebrow mt-1">{t.sector}</div>}
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <div className="text-center">
                      <div className="font-display text-2xl font-black text-[#2596be]">{row.submission_count}</div>
                      <div className="label-eyebrow">Submissions</div>
                    </div>
                    <div className="text-center">
                      <div className="font-display text-2xl font-black text-[#f4b847] inline-flex items-center gap-1">
                        <Trophy size={16} /> {row.winning_submissions}
                      </div>
                      <div className="label-eyebrow">Wins</div>
                    </div>
                    {row.avg_ai_score != null && (
                      <div className="text-center">
                        <div className="font-display text-2xl font-black text-[#A23232]">{row.avg_ai_score}</div>
                        <div className="label-eyebrow">Avg AI</div>
                      </div>
                    )}
                  </div>
                </header>

                {/* Members */}
                <div className="mt-5">
                  <div className="label-eyebrow mb-2">Members</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-[#E5E7EB]">
                    {row.members.length === 0 ? (
                      <div className="col-span-full p-3 text-xs text-[#525252] italic border-r border-b border-[#E5E7EB] bg-[#FAFAFA]">No members yet</div>
                    ) : row.members.map((m) => (
                      <div key={m.id} className="border-r border-b border-[#E5E7EB] p-3 bg-white" data-testid={`team-${t.id}-member-${m.id}`}>
                        <div className="label-eyebrow flex items-center gap-1">
                          {m.is_team_leader && <Crown size={11} className="text-[#f4b847]" />}
                          {m.is_team_leader ? "Team Leader" : "—"}
                        </div>
                        <div className="text-sm font-semibold truncate mt-1">{m.name}</div>
                        <div className="text-[10px] text-[#525252] truncate">{m.email}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Target */}
                {(row.final_target || (row.target_shortlist && row.target_shortlist.length > 0)) && (
                  <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="border-l-2 border-[#A23232] bg-[#FDEDED] p-3">
                      <div className="label-eyebrow flex items-center gap-2 text-[#7A1F1F]">
                        <Target size={12} /> Target {row.final_target ? "· FINAL" : "· shortlist"}
                      </div>
                      {row.final_target ? (
                        <>
                          <div className="font-display text-lg font-bold mt-1">{row.final_target.name}</div>
                          {row.final_target.sector && <div className="text-[10px] uppercase tracking-wider text-[#525252]">{row.final_target.sector}</div>}
                          {row.final_target.final_rationale && (
                            <p className="mt-2 text-xs text-[#0A0A0A] whitespace-pre-wrap leading-relaxed">{row.final_target.final_rationale}</p>
                          )}
                        </>
                      ) : (
                        <ul className="mt-2 text-xs space-y-1">
                          {row.target_shortlist.map((s, i) => (
                            <li key={i}>· <strong>{s.name}</strong>{s.sector ? <span className="text-[#525252]"> — {s.sector}</span> : null}</li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {row.latest_ceo_report && (
                      <div className="border-l-2 border-[#2596be] bg-[#E6F4FA] p-3">
                        <div className="label-eyebrow flex items-center gap-2 text-[#1e7da3]">
                          <FileText size={12} /> Latest team reflection
                        </div>
                        {row.latest_ceo_report.headline && (
                          <div className="font-semibold text-sm mt-1">{row.latest_ceo_report.headline}</div>
                        )}
                        {row.latest_ceo_report.summary && (
                          <p className="mt-2 text-xs text-[#0A0A0A] whitespace-pre-wrap leading-relaxed line-clamp-6">
                            {row.latest_ceo_report.summary}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {!row.final_target && (!row.target_shortlist || row.target_shortlist.length === 0) && !row.latest_ceo_report && (
                  <div className="mt-5 text-xs text-[#525252] italic">No team reflection or notes yet.</div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
